import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Play, RefreshCcw } from "lucide-react";

import { DataTable } from "@/components/console/DataTable";
import { CredentialTable } from "@/components/console/CredentialTable";
import { KeyValueGrid } from "@/components/console/KeyValueGrid";
import { PageHeader } from "@/components/console/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ApplyLogFeed } from "@/components/wizard/ApplyLogFeed";
import { PageTransition } from "@/components/wizard/PageTransition";
import { StepNavigation } from "@/components/wizard/StepNavigation";
import { WizardVerdictBanner } from "@/components/wizard/WizardVerdictBanner";
import { useWizardApply, useWizardHealth, useWizardPreview, useWizardState } from "@/hooks/wizardQueries";
import { useWizardSession } from "@/hooks/useWizardSession";
import { formatTimestamp, mapOutcomeToLevel } from "@/lib/format";
import {
  getPendingChanges,
  getRelevantCredentials,
  getUnchangedCount,
  hasPendingChanges,
} from "@/lib/operations";
import {
  getApplyButtonLabel,
  getApplyHint,
  getNoPendingHint,
  getReviewBanner,
  getReviewPageDescription,
  isBootstrapWorkPending,
} from "@/lib/reviewContext";
import { strings } from "@/lib/strings";
import { wizardRoutePath } from "@/lib/steps";
import { toast } from "sonner";

export function ReviewPage() {
  const wizardState = useWizardState();
  const preview = useWizardPreview();
  const apply = useWizardApply();
  const health = useWizardHealth();
  const session = useWizardSession();
  const isReady = health.data?.isReady ?? false;
  const [showAllOperations, setShowAllOperations] = useState(false);
  const [showAllCredentials, setShowAllCredentials] = useState(false);

  const previewData = session.previewResult;
  const applyData = session.applyResult;
  const displayData = applyData ?? previewData;
  const pendingChanges = getPendingChanges(displayData);
  const unchangedCount = getUnchangedCount(displayData);
  const pending = hasPendingChanges(displayData);
  const relevantCredentials = getRelevantCredentials(displayData, pendingChanges);
  const hasApply = !!applyData;
  const isApplying = apply.isPending;
  const isPreviewing = preview.isPending;

  useEffect(() => {
    if (!session.previewResult && !preview.isPending && !session.applyResult) {
      void runPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runPreview() {
    try {
      const result = await preview.mutateAsync();
      session.setPreviewResult(result);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : strings.toast.previewFailed);
    }
  }

  async function executeApply() {
    if (!isReady) {
      toast.error(strings.toast.readinessFailed);
      return;
    }

    session.resetApplyLogs();
    session.appendApplyLog({ level: "info", message: "POST /api/bridge/wizard/apply" });

    try {
      const response = await apply.mutateAsync({ dryRun: false });
      session.setApplyResult(response);
      session.setPreviewResult(response);
      response.operations?.forEach((op) =>
        session.appendApplyLog({
          level: mapOutcomeToLevel(op.outcome),
          stage: op.stage,
          message: op.message,
        }),
      );
      session.appendApplyLog({ level: "success", message: "Apply completed." });
      toast.success(strings.toast.applyCompleted);
    } catch (err) {
      const message = err instanceof Error ? err.message : strings.toast.applyFailed;
      session.appendApplyLog({ level: "error", message });
      toast.error(message);
    }
  }

  const bootstrapWorkPending = isBootstrapWorkPending(wizardState.data, undefined);

  const banner = getReviewBanner({
    state: wizardState.data,
    bootstrapWorkPending,
    hasApply,
    pendingCount: pendingChanges.length,
  });

  const pageDescription = getReviewPageDescription({
    bootstrapWorkPending,
    hasApply,
  });

  const noPendingHint = getNoPendingHint({
    bootstrapWorkPending,
    updatePending: wizardState.data?.updatePending ?? false,
  });
  const applyHint = getApplyHint({
    bootstrapWorkPending,
    hasApply,
    pendingCount: pendingChanges.length,
  });

  const applyButtonLabel = getApplyButtonLabel({
    isApplying,
    hasApply,
    bootstrapWorkPending,
    firstStartPending: wizardState.data?.firstStartPending ?? false,
  });

  const applyDisabled = !displayData || !isReady || isApplying || hasApply || isPreviewing;

  return (
    <PageTransition routeKey="review">
      <PageHeader
        title={strings.pages.review.title}
        description={pageDescription}
        actions={
          <Button variant="outline" onClick={runPreview} disabled={isPreviewing || isApplying}>
            <RefreshCcw className="size-4" />
            {isPreviewing ? strings.pages.review.running : strings.pages.review.rerun}
          </Button>
        }
      />

      <WizardVerdictBanner verdict={banner.verdict} />

      <p className="text-sm text-muted-foreground">{banner.description}</p>

      <KeyValueGrid
        items={[
          { label: "Organization", value: displayData?.organizationName ?? "—" },
          { label: "Project", value: displayData?.projectName ?? "—" },
          { label: "Generated", value: formatTimestamp(displayData?.generatedAt), mono: true },
          {
            label: strings.pages.review.pendingChanges,
            value: String(pendingChanges.length),
          },
          { label: "Unchanged", value: String(unchangedCount) },
        ]}
        columns={3}
        loading={isPreviewing && !displayData}
      />

      <Card className="panel">
        <CardHeader className="flex-row items-center justify-between gap-3 pb-3">
          <CardTitle className="text-base">{strings.pages.review.pendingChanges}</CardTitle>
          {pendingChanges.length ? (
            <Badge variant="outline" className="font-mono">
              {strings.pages.review.pendingBadge(pendingChanges.length)}
            </Badge>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-4">
          {!pending && displayData ? (
            <div className="rounded-lg border border-border/60 bg-secondary/20 p-4 text-sm text-muted-foreground">
              <p className="font-medium text-foreground">{strings.pages.review.noPendingChanges}</p>
              <p className="mt-1">{noPendingHint}</p>
            </div>
          ) : null}
          <DataTable
            operations={pendingChanges}
            emptyLabel={strings.pages.review.noPendingChanges}
            filterable={false}
          />
          {displayData && displayData.operations.length > pendingChanges.length ? (
            <div>
              <button
                type="button"
                className="flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setShowAllOperations((value) => !value)}
              >
                {showAllOperations ? (
                  <ChevronDown className="size-3.5" />
                ) : (
                  <ChevronRight className="size-3.5" />
                )}
                {strings.pages.review.allOperations}{" "}
                <span className="font-mono">
                  {strings.pages.review.allOperationsBadge(displayData.operations.length)}
                </span>
              </button>
              {showAllOperations ? (
                <div className="mt-3">
                  <DataTable operations={displayData.operations} emptyLabel={strings.table.noOperations} />
                </div>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {displayData?.provisionedCredentials.length && !hasApply ? (
        <section className="space-y-3">
          <button
            type="button"
            className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
            onClick={() => setShowAllCredentials((value) => !value)}
          >
            {showAllCredentials ? (
              <ChevronDown className="size-3.5" />
            ) : (
              <ChevronRight className="size-3.5" />
            )}
            {showAllCredentials
              ? strings.pages.review.allApplications
              : strings.pages.review.applications}
          </button>
          <CredentialTable
            credentials={showAllCredentials ? displayData.provisionedCredentials : relevantCredentials}
          />
        </section>
      ) : null}

      <Card className="panel">
        <CardContent className="space-y-4 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <h2 className="text-sm font-semibold">{applyButtonLabel}</h2>
              <p className="text-xs text-muted-foreground">{applyHint}</p>
            </div>
            <Button size="lg" variant={bootstrapWorkPending ? "default" : "outline"} onClick={executeApply} disabled={applyDisabled}>
              <Play className="size-4" />
              {applyButtonLabel}
            </Button>
          </div>
          <Progress
            value={isApplying ? 45 : hasApply ? 100 : displayData ? 15 : 0}
            className={isApplying ? "opacity-80" : ""}
          />
          <ApplyLogFeed logs={session.applyLogs} />
        </CardContent>
      </Card>

      <StepNavigation
        nextPath={wizardRoutePath("secrets")}
        nextLabel={strings.pages.review.nextLabel}
        nextDisabled={!hasApply}
        nextDisabledReason={
          !displayData
            ? strings.pages.review.nextDisabledPreview
            : strings.pages.review.nextDisabledApply
        }
      />
    </PageTransition>
  );
}
